/**
 * 抽獎回流頁輪詢的純決策函式。與伺服器溝通（ajax/setTimeout）留給呼叫端，
 * 這裡只回答「現在該顯示什麼、要不要停」。
 */

/** 伺服器未回 polling 節奏、或傳輸失敗重試時的保守預設（信用卡節奏） */
export const DEFAULT_INTERVAL_MS = 10000;

/**
 * @param {string} state 'paying' | 'resolving' | 'done' | 'failed'
 * @param {?string} [message] 伺服器帶的訊息（目前只有 failed 會帶）
 * @returns {string} 顯示給使用者的文字
 */
export function statusMessage(state, message) {
    switch (state) {
        case 'paying':
            return '確認付款中';
        case 'resolving':
            return '開獎中';
        case 'done':
            return '';
        case 'failed':
            return message || '本次抽獎未成立，請至訂單查看';
        default:
            return '';
    }
}

/**
 * 逾上限（自回流頁載入起算的 elapsed，不吃鎖到期時間、不用絕對時間戳）且尚未 done，
 * 視為逾時失敗——藍新沒有「處理中」這格，硬性上限是唯一收斂點。
 * @param {string} state
 * @param {number} elapsedMs
 * @param {number} hardCapMs
 * @returns {boolean}
 */
export function isTimedOut(state, elapsedMs, hardCapMs) {
    return state !== 'done' && elapsedMs >= hardCapMs;
}

/**
 * 是否該停止輪詢：done 一定停；failed 是後端判定的終局態，不待逾上限；逾上限也停
 * （顯示逾時訊息，不再徒勞輪詢）。
 * 唯一呼叫端（pollDrawStatus）在此之前已提早攔下 failed，故該分支在那支呼叫端
 * 恆不可及——這裡仍保留是把函式契約釘住，不是死碼；提早攔的理由見呼叫端註解。
 * @param {string} state
 * @param {number} elapsedMs
 * @param {number} hardCapMs
 * @returns {boolean}
 */
export function shouldStopPolling(state, elapsedMs, hardCapMs) {
    return state === 'done' || state === 'failed' || isTimedOut(state, elapsedMs, hardCapMs);
}

/**
 * 下一次輪詢間隔：以伺服器這次回應帶的 polling.interval_ms 為準（依付款方式
 * 信用卡 10 秒／離線付款 60 秒，可能隨進度回應變動），缺值時保守回信用卡節奏。
 * @param {?{interval_ms?: number}} polling
 * @returns {number}
 */
export function nextIntervalMs(polling) {
    return polling && typeof polling.interval_ms === 'number' ? polling.interval_ms : DEFAULT_INTERVAL_MS;
}

/**
 * `sessionStorage['wtoy_draw_paying']` 只存訂單 id，沒帶商品資訊——使用者中途離開後
 * 在另一個抽獎商品頁載入，會對舊訂單輪詢、甚至把舊訂單的中獎結果畫在不相干的商品頁上。
 * 存入時一併帶 productId，讀取時比對目前頁面商品，對不上就不可使用（呼叫端應清掉）。
 * 商品 id 以字串比對：sessionStorage 內存的可能是字串或數字，頁面端給的也可能是兩者之一。
 * @param {?{orderId: number, productId: number|string}} stored
 * @param {number|string} currentProductId
 * @returns {boolean}
 */
export function shouldUseStoredDrawOrder(stored, currentProductId) {
    return !!stored && String(stored.productId) === String(currentProductId);
}

/**
 * 傳輸層失敗（ajax reject）時要不要放棄。伺服器沒回應就拿不到 hard_cap_ms，
 * 改用最近一次成功回應記下的值；一次都沒成功過就無從判定，維持重試。
 * @param {number} elapsedMs
 * @param {?number} lastHardCapMs
 * @returns {boolean}
 */
export function shouldStopOnTransportFailure(elapsedMs, lastHardCapMs) {
    return typeof lastHardCapMs === 'number' && elapsedMs >= lastHardCapMs;
}
